// src/components/ModalRoot.js
import React, { useContext, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSwipeable } from 'react-swipeable';
import { RemoveScroll } from 'react-remove-scroll';

import { ModalContext } from '../context/ModalContext';
import { TabContext }   from '../context/TabContext';

import './ModalRoot.css';

export default function ModalRoot() {
  const { modalStacks, closeModal } = useContext(ModalContext);
  const { activeTab }               = useContext(TabContext);

  // ─── Ref para evitar múltiples RAFs ───────────────────────────────────
  const rafIdRef = useRef(null);
  const keyboardOpenRef = useRef(false);

  useEffect(() => {
    const HEADER_H     = 60;
    const FOOTER_H     = 60;
    const KB_THRESHOLD = 150;   // px para detectar teclado virtual

    // Actualiza solo top/bottom según teclado abierto/cerrado
    function updateVars() {
      const innerH = window.innerHeight;
      const vv = window.visualViewport;
      const viewH = vv ? vv.height : innerH;
      const kbH = innerH - viewH;
      const nowOpen = kbH > KB_THRESHOLD;

      // Si no cambia el estado de teclado, no hacemos nada
      if (keyboardOpenRef.current === nowOpen) return;
      keyboardOpenRef.current = nowOpen;

      // Si teclado abierto: full overlay (solo safe-area)
      // Si cerrado: descontar header+footer
      const topVal = nowOpen
        ? `env(safe-area-inset-top,0px)`
        : `calc(env(safe-area-inset-top,0px) + ${HEADER_H}px)`;
      const bottomVal = nowOpen
        ? `env(safe-area-inset-bottom,0px)`
        : `calc(env(safe-area-inset-bottom,0px) + ${FOOTER_H}px)`;

      // Inyectar en CSS variables
      document.documentElement.style.setProperty('--modal-top',    topVal);
      document.documentElement.style.setProperty('--modal-bottom', bottomVal);
    }

    // Agrupa llamadas a updateVars con requestAnimationFrame
    function scheduleUpdate() {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(updateVars);
    }

    // Inicializamos y nos suscribimos a eventos relevantes
    scheduleUpdate();
    window.addEventListener('resize',            scheduleUpdate);
    window.addEventListener('orientationchange', scheduleUpdate);
    document.addEventListener('focusin',         scheduleUpdate);
    document.addEventListener('focusout',        scheduleUpdate);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleUpdate);
      window.visualViewport.addEventListener('scroll', scheduleUpdate);
    }

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('resize',            scheduleUpdate);
      window.removeEventListener('orientationchange', scheduleUpdate);
      document.removeEventListener('focusin',         scheduleUpdate);
      document.removeEventListener('focusout',        scheduleUpdate);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', scheduleUpdate);
        window.visualViewport.removeEventListener('scroll', scheduleUpdate);
      }
    };
  }, []);

  // ─── Swipe para cerrar ────────────────────────────────────────────────
  const stacks = Object.entries(modalStacks);
  const swipeHandlers = useSwipeable({
    onSwiping: ({ dir, deltaX, event }) => {
      if (event.target.closest('.embla-carousel__viewport')) return;
      if (dir === 'Right') event.currentTarget.style.transform = `translateX(${deltaX}px)`;
    },
    onSwiped: ({ dir, deltaX, event }) => {
      if (event.target.closest('.embla-carousel__viewport')) return;
      const el = event.currentTarget;
      if (dir === 'Right' && deltaX > 200) closeModal(activeTab);
      else el.style.transform = 'translateX(0)';
    },
    delta: 10,
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
    trackMouse: false,
  });

  // ─── Si no hay modales, nada que renderizar ───────────────────────────
  const anyOpen = stacks.some(([, stack]) => stack.length);
  if (!anyOpen) return null;

  // ─── Renderizamos overlay que controla el scroll global ────────────────
  return ReactDOM.createPortal(
    <>
      {stacks.map(([tab, stack]) =>
        stack.map((modal, idx) => {
          const isActive = tab === activeTab && idx === stack.length - 1;
          const zIndex = 1000 + (tab === activeTab ? idx : 0);

          return (
            <div
              key={`${tab}-${modal.id}`}
              className={`modal-overlay ${modal.overlayClassName || ''} ${isActive ? 'is-active' : ''}`}
              style={{ zIndex }}
              onClick={isActive ? () => closeModal(activeTab) : undefined}
            >
              <RemoveScroll enabled={isActive}>
                <div
                  className="modal-content"
                  {...(isActive && !modal.disableSwipe ? swipeHandlers : {})}
                  onClick={e => e.stopPropagation()}
                >
                  {modal.content}
                </div>
              </RemoveScroll>
            </div>
          );
        })
      )}
    </>,
    document.getElementById('modal-root')
  );
}